import parsePhoneNumber, { CountryCode } from 'libphonenumber-js'
import { useContext, useEffect, useState } from 'react'
import { FieldValues, Path, PathValue, useFormContext } from 'react-hook-form'
import { LocaleContext } from 'components/common/LocaleContext'
import {
  InputErrorLabel,
  InputLabel,
  InputLabelBlock,
  StyledInput
} from 'components/common/TextInput'
import PhoneCountries from './PhoneCountries'
import { PhoneNumberInputStyle } from './PhoneNumberInput.style'

interface PhoneNumberInputProps<T> {
  inputLabel?: string
  errorMessage?: string
  fieldName: Path<T>
  fullSize?: boolean
  setInvalidFormatError: () => void
  disabled?: boolean
  noMargin?: boolean
}

export const PhoneNumberInput = <T extends FieldValues>({
  inputLabel,
  errorMessage,
  fieldName,
  fullSize,
  setInvalidFormatError,
  disabled,
  noMargin
}: PhoneNumberInputProps<T>) => {
  const { setValue, register, clearErrors, getValues, watch } =
    useFormContext<T>()
  const { countryCode } = useContext(LocaleContext)
  const [selectedPhoneLocale, setSelectedPhoneLocale] =
    useState<CountryCode>('FR')
  useEffect(() => {
    const value = getValues(fieldName)
    if (value) {
      setSelectedPhoneLocale(parsePhoneNumber(value)?.country ?? 'FR')
    } else {
      setSelectedPhoneLocale(countryCode)
    }
  }, [getValues(fieldName)])

  const [phoneNumberInputFieldValue, setPhoneNumberInputFieldValue] =
    useState<string>(getValues(fieldName))

  const hiddenFieldValue = watch(fieldName)
  useEffect(() => {
    setPhoneNumberInputFieldValue(hiddenFieldValue)
  }, [hiddenFieldValue])

  useEffect(() => {
    if (phoneNumberInputFieldValue) {
      try {
        const formattedPhoneNumber = parsePhoneNumber(
          phoneNumberInputFieldValue,
          selectedPhoneLocale
        )
        if (
          formattedPhoneNumber &&
          formattedPhoneNumber.isPossible() &&
          formattedPhoneNumber.isValid()
        ) {
          setValue(
            fieldName,
            formattedPhoneNumber.number as PathValue<T, Path<T>>
          )
          clearErrors(fieldName)
        } else {
          throw new Error()
        }
      } catch (error) {
        setInvalidFormatError()
      }
    }
  }, [phoneNumberInputFieldValue, selectedPhoneLocale])

  return (
    <InputLabelBlock fullSize={fullSize} noMargin={noMargin}>
      {inputLabel && <InputLabel>{inputLabel}</InputLabel>}
      <div style={{ display: 'flex' }}>
        <PhoneCountries
          selectedPhoneLocale={selectedPhoneLocale}
          setSelectedPhoneLocale={setSelectedPhoneLocale}
          disabled={disabled}
        ></PhoneCountries>
        <StyledInput
          error={!!errorMessage}
          style={PhoneNumberInputStyle}
          value={phoneNumberInputFieldValue ?? ''}
          onChange={(event) =>
            setPhoneNumberInputFieldValue(event.target.value)
          }
          disabled={disabled}
        />
      </div>

      <input hidden {...register(fieldName)}></input>
      {errorMessage && <InputErrorLabel>{errorMessage}</InputErrorLabel>}
    </InputLabelBlock>
  )
}
